import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { UPDATE_SOCIAL_PROFILE } from '../../graphql/mutations';
import { showInkToast } from '../cms/InkToast';
import { useChannelConnect } from './ChannelPicker';
import {
  Channel,
  channelMark,
  channelName,
  POSTABLE,
  REACH_CHANNELS,
  SET_APPROVAL_MODE,
} from './reach';

/** Platforms where one sign-in is one account, so a connected one leaves the Add list. */
const ONE_PER_SIGN_IN = new Set(['x', 'twitter', 'threads', 'tiktok']);

/**
 * Reach → Channels ("TinyCMS UX optimization review v6", Prototype v6): the
 * connected social accounts, each with whether the agent asks first or posts on
 * its own, and the channels that can still be added.
 *
 * Connecting: the sign-in popup comes back with the channels it can post to
 * (a Facebook sign-in may hold several Pages and their Instagram accounts);
 * the person picks, and only then do rows exist (useChannelConnect).
 * Disconnecting keeps the row (is_active = false) so its posts stay attached,
 * and connecting the same page again brings that row back.
 */
export function ReachChannels() {
  const { accountId } = useAccount();
  const { data, refetch } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [setMode] = useMutation(SET_APPROVAL_MODE);
  const [update] = useMutation(UPDATE_SOCIAL_PROFILE);
  const [busy, setBusy] = useState<string | null>(null);
  const { start, picker } = useChannelConnect({
    accountId,
    onConnected: () => void refetch(),
  });

  const channels = (data?.socialProfiles || []).filter((c) => c.isActive);
  const connectedPlatforms = new Set(channels.map((c) => c.platform.toLowerCase()));

  const changeMode = async (c: Channel, mode: 'ask' | 'auto') => {
    if (c.approvalMode === mode) return;
    setBusy(c.id);
    try {
      await setMode({ variables: { id: c.id, mode } });
      await refetch();
      showInkToast(
        mode === 'ask'
          ? `${channelName(c.platform)}: your agent asks you first.`
          : `${channelName(c.platform)}: your agent posts on its own.`
      );
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (c: Channel) => {
    if (
      !window.confirm(
        `Disconnect ${channelName(c.platform)}? Nothing more is posted there until you connect it again.`
      )
    )
      return;
    setBusy(c.id);
    try {
      await update({
        variables: {
          id: c.id,
          input: {
            profileName: c.profileName,
            profileUsername: c.profileUsername,
            profileLink: c.profileLink,
            isPublishing: false,
            isActive: false,
          },
        },
      });
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(null);
    }
  };

  const addable = POSTABLE.filter(
    (p) =>
      !ONE_PER_SIGN_IN.has(p) ||
      !(connectedPlatforms.has(p) || (p === 'x' && connectedPlatforms.has('twitter')))
  );

  return (
    <div className="rch">
      <section>
        <h2 className="rch-h">Connected</h2>
        <p className="ink-muted rch-sub">
          For each channel, choose whether your agent asks you first or posts on its own.
        </p>
        {channels.length ? (
          <div className="rch-list">
            {channels.map((c) => (
              <div className="rch-row" key={c.id}>
                <span className="rc-mark">{channelMark(c.platform)}</span>
                <div className="rch-name">
                  <div>{channelName(c.platform)}</div>
                  <div className="ink-muted">
                    {c.profileUsername
                      ? `@${c.profileUsername.replace(/^@/, '')}`
                      : c.profileName || ''}
                  </div>
                </div>
                <span
                  className="ink-seg rch-seg"
                  role="radiogroup"
                  aria-label={`${channelName(c.platform)} approvals`}
                >
                  {(
                    [
                      ['ask', 'Ask me first'],
                      ['auto', 'Post on its own'],
                    ] as const
                  ).map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      role="radio"
                      aria-checked={(c.approvalMode || 'ask') === mode}
                      data-active={(c.approvalMode || 'ask') === mode ? 'true' : 'false'}
                      disabled={busy === c.id}
                      onClick={() => void changeMode(c, mode)}
                    >
                      {label}
                    </button>
                  ))}
                </span>
                <button
                  type="button"
                  className="ink-quiet-link"
                  disabled={busy === c.id}
                  onClick={() => void disconnect(c)}
                >
                  Disconnect
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="rch-none">No channels connected yet.</p>
        )}
      </section>

      <section style={{ marginTop: 36 }}>
        <h2 className="rch-h">Add a channel</h2>
        <div className="rch-add">
          {addable.map((p) => (
            <button
              key={p}
              type="button"
              className="rch-add-btn"
              disabled={!accountId}
              onClick={() => void start(p)}
            >
              <span className="rc-mark rc-mark-sm">{channelMark(p)}</span>
              {channelName(p)}
              {connectedPlatforms.has(p) ? <span className="ink-muted"> · add another</span> : null}
            </button>
          ))}
        </div>
        <p className="ink-muted rch-sub" style={{ marginTop: 10 }}>
          You’ll sign in to the channel in a new window. One Facebook sign-in covers your Pages and
          the Instagram accounts linked to them; you pick which to connect.
        </p>
      </section>

      {picker}
    </div>
  );
}
